import { useQuery } from '@tanstack/vue-query';
import AdyenPlugin, { posSessionTarget } from '@/capacitor-plugins/AdyenPlugin';
import { Capacitor } from '@capacitor/core';
import { computed, type MaybeRefOrGetter, onMounted, ref, toValue } from 'vue';

let adyenPosSessionInit: Promise<boolean> | null = null;
let adyenPosSessionInitRun = 0;

export function ensureAdyenPosSession(): Promise<boolean> {
  if (!Capacitor.isPluginAvailable('Adyen')) {
    return Promise.resolve(false);
  }

  if (adyenPosSessionInit) {
    return adyenPosSessionInit;
  }

  const initRun = ++adyenPosSessionInitRun;
  adyenPosSessionInit = (async () => {
    try {
      const { needsTapToPayLinking } = await AdyenPlugin.needsTapToPayLinking();
      await AdyenPlugin.initPosSession().catch((error) => {
        console.error('Failed to initialize Adyen POS session', error);
      });
      if (needsTapToPayLinking) {
        await AdyenPlugin.linkTapToPay().catch((error) => {
          console.error('Failed to link tap to pay', error);
        });
      }
      return true;
    } finally {
      // An abandoned init settling late must not drop the one that replaced it
      if (adyenPosSessionInitRun === initRun) {
        adyenPosSessionInit = null;
      }
    }
  })();

  return adyenPosSessionInit;
}

type PosSessionBooking = { id: string; legalEntity: { id: string } };

let lastPosSessionOpen: Promise<unknown> = Promise.resolve();
let isLastPosSessionOpenSettled = true;
let shouldResetSession = false;
/** Bumped on abandon, so opens still queued behind the hung one never start. */
let posSessionQueueGeneration = 0;
/** Legal entity of the open at the head of the queue, once it has started. */
let runningOpenLegalEntityId: string | undefined;
/** Opens the user gave up on that have not settled yet, with their legal entity. */
const abandonedOpens = new Map<Promise<unknown>, string | undefined>();
/** Native payments that have not settled yet (e.g. ones the user cancelled) and their legal entity. */
const pendingNativePayments = new Map<Promise<unknown>, string>();

/**
 * Thrown when a booking of another legal entity is to be paid while a cancelled POS session open or
 * native payment is still running. The native SDK cannot be aborted and could finish that work on
 * the session of the newly targeted company, i.e. settle a payment on the wrong merchant account.
 * Retries for the same legal entity are never blocked.
 */
export class AdyenPosBusyError extends Error {}

function isBlockedForLegalEntity(legalEntityId: string) {
  return [...abandonedOpens.values(), ...pendingNativePayments.values()].some(
    (pendingLegalEntityId) => pendingLegalEntityId !== legalEntityId,
  );
}

/**
 * Opens the POS session for the given booking, on the merchant account of its legal entity. A
 * session of another legal entity is reset first, so a payment never lands on another company's
 * merchant account.
 *
 * Calls run one after another: the payment buttons can be tapped while the warm-up on page open is
 * still running, and a second reset would tear down the session that warm-up is opening.
 */
export function ensureAdyenPosSessionFor(booking: PosSessionBooking): Promise<boolean> {
  const generation = posSessionQueueGeneration;
  const open = lastPosSessionOpen.then(async () => {
    if (!Capacitor.isPluginAvailable('Adyen')) {
      return false;
    }
    // An abandoned open must not touch the session state any more, not even when it resumes late
    const throwIfAbandoned = () => {
      if (generation !== posSessionQueueGeneration) {
        throw new AdyenPosBusyError();
      }
    };
    throwIfAbandoned();
    if (isBlockedForLegalEntity(booking.legalEntity.id)) {
      throw new AdyenPosBusyError();
    }
    runningOpenLegalEntityId = booking.legalEntity.id;
    posSessionTarget.bookingId = booking.id;
    if (shouldResetSession || posSessionTarget.legalEntityId !== booking.legalEntity.id) {
      shouldResetSession = false;
      await AdyenPlugin.resetSession();
      throwIfAbandoned();
      posSessionTarget.legalEntityId = booking.legalEntity.id;
    }
    return ensureAdyenPosSession();
  });
  const queued = open.catch(() => undefined);
  lastPosSessionOpen = queued;
  isLastPosSessionOpenSettled = false;
  queued.then(() => {
    if (lastPosSessionOpen === queued) {
      isLastPosSessionOpenSettled = true;
    }
  });
  return open;
}

/**
 * Stops queueing behind a POS session open that never settled, after the user cancelled a payment
 * stuck on loading. The next open resets the session and starts from scratch.
 *
 * The hung native calls keep running and could still install a session for its legal entity, so
 * until it settles, opens for another legal entity are refused. Same-company retries go through.
 */
export function abandonAdyenPosSession() {
  if (isLastPosSessionOpenSettled) {
    return;
  }
  const abandoned = lastPosSessionOpen;
  abandonedOpens.set(abandoned, runningOpenLegalEntityId ?? posSessionTarget.legalEntityId);
  abandoned.then(() => abandonedOpens.delete(abandoned));

  posSessionQueueGeneration++;
  lastPosSessionOpen = Promise.resolve();
  isLastPosSessionOpenSettled = true;
  adyenPosSessionInit = null;
  shouldResetSession = true;
}

/** Starts the native payment for a booking of the given legal entity. */
export function initializeAdyenPayment(
  params: Parameters<typeof AdyenPlugin.initializePayment>[0],
  legalEntityId: string,
): Promise<any> {
  if (isBlockedForLegalEntity(legalEntityId)) {
    return Promise.reject(new AdyenPosBusyError());
  }
  const payment = AdyenPlugin.initializePayment(params);
  pendingNativePayments.set(payment, legalEntityId);
  payment.catch(() => undefined).then(() => pendingNativePayments.delete(payment));
  return payment;
}

/** Payment methods of the POS session, opened for the given booking if any. */
export function useAdyenPaymentMethods(booking?: MaybeRefOrGetter<PosSessionBooking | undefined>) {
  const shouldLoadPaymentMethods = ref(false);

  onMounted(async () => {
    const target = toValue(booking);
    // A refused open (AdyenPosBusyError) just leaves the payment methods unloaded
    shouldLoadPaymentMethods.value = await (
      target ? ensureAdyenPosSessionFor(target) : ensureAdyenPosSession()
    ).catch(() => false);
  });

  return useQuery({
    queryKey: ['adyenMethods'],
    queryFn: () => AdyenPlugin.getMethods(),
    enabled: computed(() => shouldLoadPaymentMethods.value),
  });
}
